/**
 * ①業務マップ — ステージ │ 業務フロー │ 業務 │ 業務ルール のレーン列。
 *
 * **仕様の全体がどうなっているかと、ある業務がどの流れから使われるかを同じ 1 画面で見る。**
 * 左のカードを押すと右が絞られるので、仕様側 4 段ぶんに 2 画面以内で辿り着く。
 *
 * ステージのレーンは読取が返した鎖の順に並べ、**鎖の切れ目に鎖の名前を出す**。鎖は入口の参照
 * から読取が導出したもので、画面は並べ直さない。どの鎖にも入らない横断ステージは最後に置く。
 *
 * 業務フローのレーンは**畳まずにステージの配下を全部出す**。ステージの順に、ステージの中は
 * メインフローを一番上（札「メイン」）、その下にサブフロー（札「サブ」＋ 役割）を並べる
 * （`orderFlowsByStage`・引継書 §3.1）。
 *
 * カードは看板形式で描く（`LaneBoard`）。カードを押すと右のレーンが絞られ、別の画面へはカード右上の
 * 「詳細」で行く（`lane-card-details.tsx`）。業務ルールのカードは右に絞る先が無いので押せず、業務ルールの
 * 画面（`/rules/$ruleId`）へも「詳細」でだけ行く（Dhik 2026-09-30「カードを押したときには何も起きない
 * ようにして。詳細を見るときには詳細ボタンをクリックに統一して」）。
 *
 * 案件を渡すと、その案件の未確定変更を重ねた姿になる。**印の出どころは案件の変更の読取
 * 1 本に絞る**（決定 7）。姿を返す `getBusinessMap` は印を持たないので、印はその読取が返す
 * `laneChanges`（共通部が決めた直接の追加 / 変更 / 削除と、配下に変更）から載せる。
 * **載せるのは直接の変更だけで、配下に変更は札も枠も付けない**（Dhik 2026-09-29「PJモードの業務マップ
 * でも配下に変更を出さず、色もつけないでほしい」）。PJ の変更一覧と同じ `withoutDescendantPjLaneChanges`
 * を通すので、どちらの画面でも同じ要素だけが印を持つ。案件が消した要素は案件を当てた姿に居ないので、
 * master の姿から戻して「削除」の印で出す（`withDeletedFromMaster`。モックの置き方）。
 *
 * **カードには名前と変更の印だけを見せる**（Dhik 2026-09-29「各画面、各もっとも大きなコンポーネント内で、
 * 本当に出すべき情報を 1 or 2 個（できれば 1 個）にするぐらい情報を絞ってほしい」）。配下の件数の行・
 * 業務ルールの実装先・空のレーンの案内・鎖の見出しは描かない（押せば右のレーンに並び、並び順で鎖は
 * 分かる）。業務フローの型（メイン ／ サブ）・業務のアクターとドメイン・未翻訳はカードに指を載せたときに
 * 出し（`laneCardHint`）、レーンの件数は見出しの `title` に、「詳細」はカードに指を載せたときだけ出す。
 * 案件を当てたときは業務のアクターを出さない（同日「業務のカードにある業務フロー、アクターの情報を削って
 * ほしい」）。見出しの横の「AIRCLOSET-N を適用した姿」は、ヘッダーの PJ モードの札が同じことを言うので置かない。
 *
 * 案件を当てたときは、見出しの行の右端に「変更したものだけ見る」のトグルを置く（`PjChangedOnlyToggle`。
 * 押している間は文言が「全体を見る」に替わる）。既定は全体。押している間は 4 レーンとも
 * 直接の変更（追加 / 変更 / 削除）の印を持つカードだけを出す（`keepDirectlyChangedLanes`）。
 *
 * 案件の変更の行（`pjChanges`）はルートが揃えてから渡す。`laneChanges` は同じ読取の同じ
 * 応答に載っているので、ここで呼び直してもキャッシュから返る（`usePjLaneChanges`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップを 4 レーンの看板形式で描き、案件を重ねた姿と案件が直接変えた要素（配下に変更は除く）の印を出し、案件を当てたときは「変更したものだけ見る」のトグルで直接の変更だけに絞れるようにする。
 * @graph-connects useBusinessMap [calls] 業務マップの取得
 * @graph-connects usePjLaneChanges [calls] 変わったレーン 4 型の取得
 * @graph-connects withoutDescendantPjLaneChanges [calls] 変わったレーン 4 型から直接の変更だけを残す
 * @graph-connects PjChangedOnlyToggle [calls] 「変更したものだけ見る」のトグル
 * @graph-connects keepDirectlyChangedLanes [calls] 「変更したものだけ見る」で直接の変更だけに絞る
 * @graph-connects LaneBoard [calls] レーン列の描画
 * @graph-connects buildPjLaneMarkIndex [calls] 変わった要素の印の索引
 * @graph-connects stageCardDetail [calls] ステージのカードの「詳細」の行き先
 * @graph-connects flowCardDetail [calls] 業務フローのカードの「詳細」の行き先
 * @graph-connects businessCardDetail [calls] 業務のカードの「詳細」の行き先
 * @graph-connects ruleCardDetail [calls] 業務ルールのカードの「詳細」の行き先
 */
import type { PjChangeRow } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../components/spec/fallback-states.js';
import { LaneBoard, type Lane, type LaneCard } from '../components/spec/LaneBoard.js';
import { LaneSelectionBar, type LaneSelectionChip } from '../components/spec/LaneSelectionBar.js';
import { PjChangedOnlyToggle } from '../components/spec/PjChangedOnlyToggle.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { useBusinessMap } from '../hooks/queries/spec-graph-lanes-queries.js';
import { usePjLaneChanges } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { flowKindLabel, laneCardHint, untranslatedHint } from './lane-card-hints.js';
import {
  businessCardDetail,
  flowCardDetail,
  ruleCardDetail,
  stageCardDetail,
} from './lane-card-details.js';
import {
  clearBusinessMapSelection,
  EMPTY_BUSINESS_MAP_SELECTION,
  groupBusinessMapStages,
  BUSINESS_MAP_SELECTION_LANES,
  businessMapParentIndex,
  keepDirectlyChangedLanes,
  orderFlowsByStage,
  selectBusinessMapLanes,
  toggleBusinessMapSelection,
  withDeletedFromMaster,
  type BusinessMapSelection,
  type BusinessMapSelectionLane,
} from './overview-selection.js';
import {
  buildPjLaneMarkIndex,
  EMPTY_PJ_LANE_MARK_INDEX,
  pjLaneMarkOf,
  type PjLaneMarkIndex,
} from './pj-lane-marks.js';
import { withoutDescendantPjLaneChanges } from './pj-lanes-build.js';

/** `OverviewPage` の props。 */
export interface OverviewPageProps {
  /** 重ねる案件番号。渡さなければ master */
  pjNumber?: number;
  /**
   * その案件の変更の行。ルートが揃えてから渡す（master のときは空の配列）。
   *
   * **印はここから畳まない。** 印は同じ読取の応答に載る `laneChanges` の直接の変更から載せる。
   * 直接か配下かを決めるのは共通部の 1 か所で、行から畳み直すと判定が 2 か所に分かれる。
   * 受け口を残すのは、渡している側（`receivers/business-map.tsx`）の配線を変えずに済ませるため
   * である — ルートの配線は別の持ち場で、外すのはそちらと揃えるときにする。
   */
  pjChanges: readonly PjChangeRow[];
}

/**
 * 業務マップのページ。
 *
 * **master と案件で部品を分ける。** 案件のときだけ変わったレーン 4 型を読むので、同じ部品に
 * 置くとフックを呼ぶかどうかが描画のたびに変わる。
 *
 * 「変更したものだけ見る」のトグルは案件のときだけ見出しの右端に置き、選択はこの部品が持つ。URL に載せない
 * のはレーンの選択と同じ理由である（`OverviewBoard`）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの見出し（案件のときは「変更したものだけ見る」のトグルを右端に）を描き、master か案件を重ねた姿の本体を出し分ける。
 * @graph-connects PjChangedOnlyToggle [calls] 「変更したものだけ見る」のトグル
 * @graph-connects OverviewMasterBody [calls] master の姿の本体
 * @graph-connects OverviewPjBody [calls] 案件を重ねた姿の本体
 */
export function OverviewPage({ pjNumber }: OverviewPageProps) {
  const { t } = useTranslation();
  const [changedOnly, setChangedOnly] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('overview.title')}
        </h1>
        {pjNumber !== undefined && (
          <PjChangedOnlyToggle changedOnly={changedOnly} onChange={setChangedOnly} />
        )}
      </header>
      {pjNumber === undefined ? (
        <OverviewMasterBody />
      ) : (
        <OverviewPjBody pjNumber={pjNumber} changedOnly={changedOnly} />
      )}
    </div>
  );
}

/**
 * master の姿の本体。印は付かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップを master の姿で取得し、印なしで出す。
 * @graph-connects useBusinessMap [calls] 業務マップの取得
 */
function OverviewMasterBody() {
  const { data, isLoading, error } = useBusinessMap();

  return (
    <OverviewBody
      data={data}
      isLoading={isLoading}
      error={error}
      marks={EMPTY_PJ_LANE_MARK_INDEX}
      pjNumber={undefined}
      changedOnly={false}
    />
  );
}

/** `OverviewPjBody` の props。 @graph-connects none */
interface OverviewPjBodyProps {
  /** 重ねる案件番号 */
  pjNumber: number;
  /** 「変更したものだけ見る」を押しているか */
  changedOnly: boolean;
}

/**
 * 案件を重ねた姿の本体。案件を当てた姿・master の姿・変わったレーン 4 型の 3 本が揃ってから描く。
 *
 * **揃うまで描かない。** 変わったレーン 4 型が来ないまま描くと、印の無いカードが「案件が
 * 何も変えていない」ものとして並ぶ。master の姿は、案件が消した要素を「削除」の印で出すのに
 * 使う（`withDeletedFromMaster`）。
 *
 * **印の索引には直接の変更だけを入れる。** 配下に変更は索引に入れず、カードは「変更なし」と同じ
 * 見た目（札も枠も無し）になる。LaneBoard の既定の振る舞いは変えず、渡す印の側で落とす。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップを案件を重ねた姿で取得し、案件が消した要素を master から戻して、変わったレーン 4 型のうち直接の変更の印だけを載せて出す。
 * @graph-connects useBusinessMap [calls] 案件を当てた姿と master の姿の取得
 * @graph-connects usePjLaneChanges [calls] 変わったレーン 4 型の取得
 * @graph-connects withoutDescendantPjLaneChanges [calls] 印を直接の変更だけに絞る
 * @graph-connects withDeletedFromMaster [calls] 案件が消した要素を master から戻す
 */
function OverviewPjBody({ pjNumber, changedOnly }: OverviewPjBodyProps) {
  const map = useBusinessMap(pjNumber);
  const master = useBusinessMap();
  const laneChanges = usePjLaneChanges(pjNumber);
  const marks = useMemo(
    () => buildPjLaneMarkIndex(withoutDescendantPjLaneChanges(laneChanges.data ?? [])),
    [laneChanges.data],
  );
  const data = useMemo(
    () =>
      map.data === undefined || master.data === undefined || laneChanges.data === undefined
        ? undefined
        : withDeletedFromMaster(map.data, master.data, laneChanges.data),
    [map.data, master.data, laneChanges.data],
  );

  return (
    <OverviewBody
      data={data}
      isLoading={map.isLoading || master.isLoading || laneChanges.isLoading}
      error={map.error ?? master.error ?? laneChanges.error}
      marks={marks}
      pjNumber={pjNumber}
      changedOnly={changedOnly}
    />
  );
}

/** `OverviewBody` の props。 @graph-connects none */
interface OverviewBodyProps {
  data: ReturnType<typeof useBusinessMap>['data'];
  isLoading: boolean;
  error: unknown;
  marks: PjLaneMarkIndex;
  pjNumber: number | undefined;
  changedOnly: boolean;
}

/**
 * 本体（失敗 / 読み込み中 / レーン列）の出し分け。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップを失敗・読み込み中・レーン列の 3 状態で出し分ける。
 * @graph-connects OverviewBoard [calls] レーン列と絞り込みの描画
 */
function OverviewBody({ data, isLoading, error, marks, pjNumber, changedOnly }: OverviewBodyProps) {
  const { t } = useTranslation();

  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }

  if (isLoading || data === undefined) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="620px" />
      </div>
    );
  }

  return <OverviewBoard map={data} marks={marks} pjNumber={pjNumber} changedOnly={changedOnly} />;
}

/** `OverviewBoard` の props。 */
export interface OverviewBoardProps {
  /** 業務マップ */
  map: NonNullable<ReturnType<typeof useBusinessMap>['data']>;
  /** 案件が直接変えた要素の印の索引（配下に変更は入れない） */
  marks: PjLaneMarkIndex;
  /**
   * 重ねている案件番号。master を見ているなら `undefined`。
   *
   * 次の画面へのリンクが案件を引き継ぐために要る。**任意の prop にしない** — 渡し忘れは
   * 型でも lint でも落ちず、その導線だけ黙ってマスターの画面を開く。
   */
  pjNumber: number | undefined;
  /**
   * 「変更したものだけ見る」を押しているか。master では常に `false`。
   *
   * `pjNumber` と同じ理由で任意の prop にしない — 渡し忘れると、トグルを押しても黙って全体が出る。
   */
  changedOnly: boolean;
}

/**
 * 絞り込みの札とレーン列。**選択はこの部品が持つ。**
 *
 * 絞り込みを URL に載せない。レーンの選択は「いま読んでいる位置」であって共有する状態では
 * なく、押すたびに履歴が積まれると戻るボタンが絞り込みの取り消しに化ける。
 *
 * 「変更したものだけ見る」は選択を当てたあとに掛ける（`keepDirectlyChangedLanes`）。業務フローの並びは絞る前の
 * ステージの順で決める — 絞ったあとのステージで並べると、変わっていないステージの配下の変わった
 * 業務フローが最後に落ちる。
 *
 * カードには名前と変更の印だけを見せる。業務フローの型・業務のアクターとドメイン・未翻訳は見出しの
 * 補足（`laneCardHint`）へ移し、案件を当てたときはアクターを補足にも出さない。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの選択を保ち、絞り込みの札と 4 レーンの看板形式のカード列（ステージは鎖の順、業務フローはメインを一番上）を名前と変更の印だけで描き、補足はカードに指を載せたときに出し、「変更したものだけ見る」なら直接の変更だけを出す。
 * @graph-connects LaneBoard [calls] レーン列の描画
 * @graph-connects selectBusinessMapLanes [calls] 選択を当てた 4 レーンの中身
 * @graph-connects keepDirectlyChangedLanes [calls] 「変更したものだけ見る」で直接の変更だけに絞る
 * @graph-connects groupBusinessMapStages [calls] ステージを鎖の順に並べる
 * @graph-connects orderFlowsByStage [calls] 業務フローをステージの順・メインを一番上に並べる
 * @graph-connects stageCardDetail [calls] ステージのカードの「詳細」の行き先
 * @graph-connects flowCardDetail [calls] 業務フローのカードの「詳細」の行き先
 * @graph-connects businessCardDetail [calls] 業務のカードの「詳細」の行き先
 * @graph-connects ruleCardDetail [calls] 業務ルールのカードの「詳細」の行き先
 * @graph-connects flowKindLabel [calls] 業務フローの型の呼び名
 * @graph-connects untranslatedHint [calls] 名前はあるが訳の欠けたカードの「未翻訳」
 * @graph-connects laneCardHint [calls] カードの補足を 1 行にする
 * @graph-connects toSelectionLane [calls] 札の鍵を段の種別に読み替える
 * @graph-connects selectionChips [calls] いま絞っている条件を札に写す
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 */
export function OverviewBoard({ map, marks, pjNumber, changedOnly }: OverviewBoardProps) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<BusinessMapSelection>(EMPTY_BUSINESS_MAP_SELECTION);
  const parents = useMemo(() => businessMapParentIndex(map), [map]);
  const selectedLanes = useMemo(() => selectBusinessMapLanes(map, selection), [map, selection]);
  const lanes = useMemo(
    () => (changedOnly ? keepDirectlyChangedLanes(selectedLanes, marks) : selectedLanes),
    [changedOnly, selectedLanes, marks],
  );
  const flows = useMemo(
    () =>
      orderFlowsByStage(
        lanes.flows,
        selectedLanes.stages.map((stage) => stage.stageId),
      ),
    [lanes.flows, selectedLanes.stages],
  );
  const stageGroups = useMemo(
    () => groupBusinessMapStages(lanes.stages, map.chains),
    [lanes.stages, map.chains],
  );

  const select = (lane: BusinessMapSelectionLane) => (id: number) => {
    setSelection((current) => toggleBusinessMapSelection(current, lane, id, parents));
  };

  /*
   * 見出しが空のカードは「未設定」「未翻訳」を主表記にする（`displayLabel`）。空文字の見出しは
   * カードが消えたように見え、id に落とすと人が判別できない番号だけが並ぶ。名前はあるが訳の欠けた
   * カードの「未翻訳」は、ほかの補足と一緒にカードに指を載せたときに出す。
   */
  const card = (
    id: number,
    label: string,
    untranslated: boolean,
    rest: Partial<LaneCard>,
    hints: readonly (string | null | undefined)[] = [],
  ) =>
    ({
      id,
      title: displayLabel(label, t, untranslated),
      hint: laneCardHint([untranslatedHint(label, untranslated, t), ...hints]),
      diff: pjLaneMarkOf(marks, id),
      ...rest,
    }) satisfies LaneCard;

  /* 鎖の見出しは置かない。鎖の順（横断ステージは最後）に並べるだけで鎖は読める。 */
  const board: readonly Lane[] = [
    {
      key: 'stage',
      title: t('overview.laneStage'),
      count: lanes.stages.length,
      selectedId: selection.stageId,
      onSelect: select('stage'),
      cards: stageGroups.flatMap((group) =>
        group.stages.map((stage) =>
          card(stage.stageId, stage.label, stage.untranslated, {
            detail: stageCardDetail(stage.stageId, pjNumber, t),
          }),
        ),
      ),
    },
    {
      key: 'flow',
      title: t('overview.laneFlow'),
      count: lanes.flows.length,
      selectedId: selection.flowId,
      onSelect: select('flow'),
      cards: flows.map((flow) =>
        card(
          flow.flowId,
          flow.label,
          flow.untranslated,
          { detail: flowCardDetail(flow.flowId, pjNumber, t) },
          [flowKindLabel(flow.role, t)],
        ),
      ),
    },
    {
      key: 'business',
      title: t('overview.laneBusiness'),
      count: lanes.businesses.length,
      selectedId: selection.businessId,
      onSelect: select('business'),
      cards: lanes.businesses.map((business) =>
        card(
          business.businessId,
          business.label,
          business.untranslated,
          { detail: businessCardDetail(business.businessId, pjNumber, t) },
          /* 案件を当てたときはアクターを出さず、ドメインだけにする。 */
          [pjNumber === undefined ? business.actor?.label : null, business.domain?.label],
        ),
      ),
    },
    {
      key: 'rule',
      title: t('overview.laneBusinessRule'),
      count: lanes.rules.length,
      cards: lanes.rules.map((rule) =>
        card(rule.ruleId, rule.label, rule.untranslated, {
          detail: ruleCardDetail(rule.ruleId, pjNumber, t),
        }),
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      <LaneSelectionBar
        chips={selectionChips(map, selection, t)}
        onClear={(key) => {
          setSelection((current) => clearBusinessMapSelection(current, toSelectionLane(key)));
        }}
        onClearAll={() => {
          setSelection(EMPTY_BUSINESS_MAP_SELECTION);
        }}
      />
      <LaneBoard lanes={board} countInTitle controlsOnHover />
    </div>
  );
}

/** 札の鍵を段の種別に読み替える。閉集合に無い鍵は業務として扱う。 @graph-connects none */
function toSelectionLane(key: string): BusinessMapSelectionLane {
  return BUSINESS_MAP_SELECTION_LANES.find((lane) => lane === key) ?? 'business';
}

/**
 * いま絞っている条件を札に写す。
 *
 * @param map 業務マップ
 * @param selection いまの選択
 * @param t 文言
 * @returns 札の並び（左のレーンから順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いまの選択を、レーンの見出しとカードの見出しを持つ札の並びへ写す。
 * @graph-connects none
 */
export function selectionChips(
  map: OverviewBoardProps['map'],
  selection: BusinessMapSelection,
  t: (key: string) => string,
): readonly LaneSelectionChip[] {
  const picked = [
    {
      key: 'stage',
      lane: 'overview.laneStage',
      item: map.stages.find((stage) => stage.stageId === selection.stageId),
    },
    {
      key: 'flow',
      lane: 'overview.laneFlow',
      item: map.flows.find((flow) => flow.flowId === selection.flowId),
    },
    {
      key: 'business',
      lane: 'overview.laneBusiness',
      item: map.businesses.find((business) => business.businessId === selection.businessId),
    },
  ];

  return picked.flatMap(({ key, lane, item }) =>
    item === undefined
      ? []
      : [{ key, lane: t(lane), label: displayLabel(item.label, t, item.untranslated) }],
  );
}
